import { AutoAwesomeOutlined, DocumentScannerOutlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, Paper } from "@mui/material";
import {
  DataGrid,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_CHECKBOX_SELECTION_FIELD,
  GridColDef,
  GridSelectionModel,
} from "@mui/x-data-grid";
import { UUID } from "crypto";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useDeepChatSocketContext } from "../../../contexts/DeepChatSocketContext";
import ChartCard from "../../ChartCard";
import { BodyPanel, MainPanel } from "../Layout/Panels";
import StudyHeader from "../Layout/StudyHeader";
import useMarketResearchStore from "../useMarketResearchStore";
import useStudyMetadataStore from "../useStudyMetadataStore";

interface UriParams {
  studyId: UUID;
}

export default function ViewRespondents() {
  const { studyId } = useParams() as unknown as UriParams;
  const navigate = useNavigate();
  const DeepChatSocket = useDeepChatSocketContext();
  const { setSelectedStudyIds, toggleFilterDrawer, toggleLoadingChat, clearChatSession, generateRespondentSummary } =
    useMarketResearchStore();
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [selectedRespondentGUID, setSelectedRespondentGUID] = useState<{ respondent_guid: string }[]>([]);
  const [selectedRespondentsIDs, setSelectedRespondentIDs] = useState<Record<string, string>[]>([]);
  const { study, isLoading } = useStudyMetadataStore(studyId as UUID);

  const respondentsArray = study?.interviews
    .filter((i) => i.transcript_file_guid !== null)
    .flatMap((i) => i.respondents.map((r) => r));

  useEffect(() => {
    toggleFilterDrawer(false);
    setSelectedStudyIds([studyId]);
  }, []);

  const data = (respondentsArray ?? []).map((_) => ({
    id: _.respondent_id,
    respondent_guid: _.respondent_guid,
    persona: _.persona,
    heir: _.heir,
    target_priority: _.target_priority,
    specialty: _.specialty,
    segment: _.practice_segment,
  }));

  const columns: GridColDef[] = [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      field: GRID_CHECKBOX_SELECTION_FIELD,
      headerName: "Select",
      renderHeader: () => <span>Select</span>,
      width: 60,
      sortable: false,
      disableColumnMenu: true,
    },
    { sortable: false, field: "id", headerName: "Respondent ID", flex: 1 },
    { sortable: false, field: "persona", headerName: "Persona", flex: 2 },
    { sortable: false, field: "heir", headerName: "HEIR", flex: 2 },
    { sortable: false, field: "target_priority", headerName: "Target Priority", flex: 2 },
    { sortable: false, field: "specialty", headerName: "Specialty", flex: 2 },
    { sortable: false, field: "segment", headerName: "Practice Segment", flex: 2 },
    {
      sortable: false,
      field: "go_to",
      headerName: "View",
      flex: 0.75,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              <DocumentScannerOutlined
                sx={{ cursor: "pointer" }}
                fontSize="small"
                onClick={() => {
                  const respondentId = params.row.respondent_guid;
                  navigate(`/market-research/study/${studyId}/transcript/${respondentId}`);
                }}
              />
            </Box>
          </Box>
        );
      },
    },
  ];

  // We want to pass undefined if nothing is selected
  const respondent_id_selection = selectedRespondentGUID.length
    ? selectedRespondentGUID.map((val) => String(val.respondent_guid))
    : undefined;

  return (
    <MainPanel>
      <BodyPanel>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Header */}
          <Box
            sx={{
              paddingBottom: "1em",
              display: "flex",
              justifyContent: "space-between",
            }}
            flexDirection={"row"}
          >
            <StudyHeader study={study} />
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Button
                variant="outlined"
                sx={{
                  color: "#003087",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onClick={() => {
                  navigate(-1);
                }}
              >
                BACK TO STUDY
              </Button>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1 }}>
            <ChartCard
              title={"Interviews"}
              backgroundColor={"white"}
              sx={{ padding: "18px 18px 0 18px", height: "100%" }}
              actions={
                <Box>
                  <Button
                    variant="text"
                    disabled={selectionModel.length === 0}
                    onClick={() => {
                      clearChatSession();
                      toggleLoadingChat(true);
                      generateRespondentSummary(DeepChatSocket.sendJsonMessage, [studyId], respondent_id_selection);
                      navigate(`/market-research/study/${studyId}/generate-summary`, {
                        state: {
                          respondent_id_selection: selectedRespondentsIDs,
                        },
                      });
                    }}
                    sx={{
                      px: "1rem",
                      color: "#003087",
                      justifyContent: "center",
                    }}
                    startIcon={<AutoAwesomeOutlined color="primary" />}
                  >
                    Generate Summary
                  </Button>
                </Box>
              }
            >
              <Box sx={{ height: "100%", paddingBottom: "1em" }}>
                <Paper sx={{ height: "100%", width: "100%" }}>
                  {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <DataGrid
                      rows={data}
                      columns={columns}
                      initialState={{ pagination: { page: 0, pageSize: 100 } }}
                      hideFooter={true}
                      sx={{
                        background: "#F7F8F8",
                        "& .MuiDataGrid-columnSeparator": {
                          display: "none",
                        },
                      }}
                      checkboxSelection
                      onSelectionModelChange={(newSelectionModel) => {
                        setSelectionModel(newSelectionModel);

                        const selectedRespondentIds = data
                          .filter((row) => newSelectionModel.includes(row.id))
                          .map((row) => {
                            return {
                              id: row.id,
                              respondent_guid: row.respondent_guid,
                            };
                          });

                        setSelectedRespondentIDs(selectedRespondentIds);

                        const selected = data
                          .filter((row) => newSelectionModel.includes(row.id))
                          .map((row) => ({
                            respondent_guid: row.respondent_guid,
                          }));
                        setSelectedRespondentGUID(selected);
                      }}
                      selectionModel={selectionModel}
                    />
                  )}
                </Paper>
              </Box>
            </ChartCard>
          </Box>
        </Box>
      </BodyPanel>
    </MainPanel>
  );
}
